import { useQuery } from "@apollo/client";
import { IonCol, IonGrid, IonRow } from "@ionic/react";
import { useMemo } from "react";
import { FormattedMessage, FormattedNumber, useIntl } from "react-intl";

import BlockAccordion from "@components/accordions/BlockAccordion";
import styles from "@components/account/Account.module.css";
import PackagePurchaseOptionPurchase from "@components/account/PackagePurchaseOptionPurchase";
import Button from "@components/buttons/Button";
import SquareCard from "@components/cards/SquareCard";
import DateStamp from "@components/dates/DateStamp";
import PackageGrantForm from "@components/forms/PackageGrantForm";
import HandleQuery from "@components/HandleQuery";
import Nbsp from "@components/Nbsp";
import PackageGrantTable from "@components/PackageGrantTable";
import BodyCopy from "@components/typography/BodyCopy";
import Heading1 from "@components/typography/Heading1";
import Heading2 from "@components/typography/Heading2";
import useMyExpert from "@hooks/useMyExpert";
import useOpener from "@hooks/useOpener";
import {
  ExpertGlobalRoleEnum,
  GetClientWithAccountQuery,
  GetPackagePurchaseOptionsDocument,
  GetPackagePurchaseOptionsQuery,
  GetPackagePurchaseOptionsQueryVariables,
  PackageGrantFragment as PackageGrant
} from "@typing/Generated";
import { currentPlanBalance, packageGrantDescription, sortByTimestamp } from "@utils/modelUtils";
import { isIosNativeApp } from "@utils/platformUtils";

type PackagePurchaseOption = GetPackagePurchaseOptionsQuery["packagePurchaseOptions"][number];

type PurchaseOptionProps = {
  index: number;
  onClick: (purchaseOption: PackagePurchaseOption) => void;
  purchaseOption: PackagePurchaseOption;
};

const PurchaseOption = ({ index, onClick, purchaseOption }: PurchaseOptionProps) => (
  <IonGrid className={`ion-no-padding ${styles.purchaseOption}`}>
    <IonRow className="ion-align-items-center">
      <IonCol className="ion-margin-end" size="auto">
        <SquareCard color={index % 2 !== 0 ? "primary-500" : "primary-100"} key={purchaseOption.id}>
          <Heading1 className="ion-text-center" color={index % 2 !== 0 ? "white" : "primary"}>
            <FormattedMessage id="pages.client.account.nHours" values={{ count: purchaseOption.minutes / 60 }} />
          </Heading1>
        </SquareCard>
      </IonCol>
      <IonCol className="ion-margin-end">
        <Heading2 bold color="gray">
          <FormattedNumber
            currency="USD"
            maximumFractionDigits={0}
            minimumFractionDigits={0}
            style="currency"
            value={purchaseOption.priceInCents / 100.0}
          />
        </Heading2>
        <div>
          <BodyCopy color="gray-500" size="small">
            <FormattedMessage id={`pages.client.account.purchaseOption${purchaseOption.minutes}.description`} />
          </BodyCopy>
        </div>
      </IonCol>
      <IonCol className={`${styles.purchaseButton} ion-text-end`} sizeMd="auto" sizeSm="12" sizeXs="12">
        <Button onClick={() => onClick(purchaseOption)}>
          <FormattedMessage id="dictionary.purchase" />
        </Button>
      </IonCol>
    </IonRow>
  </IonGrid>
);

type Props = {
  clearCurrentPackagePurchaseOption: () => void;
  client: GetClientWithAccountQuery["client"];
  currentPackagePurchaseOption: PackagePurchaseOption | null;
  generateHandlePackagePurchaseOptionSelect: (purchaseOption: PackagePurchaseOption) => void;
};

const AccountPackage = ({
  clearCurrentPackagePurchaseOption,
  client,
  currentPackagePurchaseOption,
  generateHandlePackagePurchaseOptionSelect
}: Props) => {
  const intl = useIntl();
  const myExpert = useMyExpert();
  const compHoursOpener = useOpener();

  const getPackagePurchaseOptionsQuery = useQuery<
    GetPackagePurchaseOptionsQuery,
    GetPackagePurchaseOptionsQueryVariables
  >(GetPackagePurchaseOptionsDocument);

  const hasUnlimited = useMemo(
    () => client.eligiblePackageGrants.some(grant => !grant.expired && grant.hasUnlimitedMinutes),
    [client.eligiblePackageGrants]
  );

  const nextExpirationInfo = useMemo(() => {
    const expiring = client.eligiblePackageGrants.filter(grant => grant.expiresAt);
    const sorted = sortByTimestamp<PackageGrant>(expiring, packageGrant => packageGrant.expiresAt as Date);
    const nextExpiring = sorted.filter(grant => (grant.expiresAt as Date) > new Date()).reverse()[0];
    if (nextExpiring) {
      const description = packageGrantDescription({
        hasCarePlanning: nextExpiring.hasCarePlanning && !nextExpiring.carePlanningCompleted,
        hasUnlimitedMinutes: nextExpiring.hasUnlimitedMinutes,
        intl,
        minutes: nextExpiring.minutesRemaining
      });

      return (
        <>
          {description}
          {nextExpiring.expiresAt && (
            <>
              <Nbsp />
              <FormattedMessage id="dictionary.expiring" />
              <Nbsp />
              <DateStamp value={nextExpiring.expiresAt} />
            </>
          )}
        </>
      );
    }
  }, [client.eligiblePackageGrants, intl]);

  return (
    <div>
      <BlockAccordion
        border={false}
        color="transparent"
        content={
          <div className="ion-margin-top">
            <PackageGrantTable packageGrants={client.eligiblePackageGrants} />
            <div className="ion-margin-top">
              <BodyCopy bold color="black" size="small">
                {currentPlanBalance(client, intl)}
              </BodyCopy>
            </div>
            {myExpert?.expertGlobalRole === ExpertGlobalRoleEnum.MANAGER && (
              <div className="ion-margin-top">
                {!compHoursOpener.isOpen && (
                  <Button onClick={compHoursOpener.open}>
                    <FormattedMessage id="dictionary.compHours" />
                  </Button>
                )}
                {compHoursOpener.isOpen && (
                  <PackageGrantForm
                    clientId={client.id}
                    onCancel={compHoursOpener.close}
                    onSuccess={compHoursOpener.close}
                  />
                )}
              </div>
            )}
          </div>
        }
        heading={
          <>
            <Heading2 bold color="black">
              <FormattedMessage id="pages.client.account.privacyAndAccount.sections.activePackages" />
            </Heading2>
            <div>
              <BodyCopy bold color="danger" size="small">
                {nextExpirationInfo}
              </BodyCopy>
            </div>
          </>
        }
      />
      <BlockAccordion
        border={false}
        color="transparent"
        content={
          <div className="ion-margin-top">
            <BodyCopy>
              <FormattedMessage id="pages.client.account.packageEducation.content1" />
            </BodyCopy>
          </div>
        }
        heading={
          <Heading2 bold color="black">
            <FormattedMessage id="pages.client.account.packageEducation.heading1" />
          </Heading2>
        }
      />
      <BlockAccordion
        border={false}
        color="transparent"
        content={
          <div>
            <BodyCopy>
              <FormattedMessage id="pages.client.account.packageEducation.content2" />
            </BodyCopy>
          </div>
        }
        heading={
          <Heading2 bold color="black">
            <FormattedMessage id="pages.client.account.packageEducation.heading2" />
          </Heading2>
        }
      />

      {!hasUnlimited && !isIosNativeApp() && (
        <>
          <div className={styles.sectionDivider} />
          <div className="ion-padding">
            <div className={styles.spacingBottom}>
              <Heading2 bold color="black">
                <FormattedMessage id="pages.client.account.extend" />
              </Heading2>
            </div>

            <HandleQuery query={getPackagePurchaseOptionsQuery}>
              {packagePurchaseOptionsData => (
                <>
                  {currentPackagePurchaseOption ? (
                    <PackagePurchaseOptionPurchase
                      clearCurrentPackagePurchaseOption={clearCurrentPackagePurchaseOption}
                      clientId={client.id}
                      packagePurchaseOption={currentPackagePurchaseOption}
                    />
                  ) : (
                    [60, 180, 420].map((minutes, index) => {
                      const purchaseOption = packagePurchaseOptionsData.packagePurchaseOptions.find(
                        option => option.minutes === minutes
                      );
                      if (!purchaseOption) return null;

                      return (
                        <PurchaseOption
                          index={index}
                          key={purchaseOption.id}
                          onClick={generateHandlePackagePurchaseOptionSelect}
                          purchaseOption={purchaseOption}
                        />
                      );
                    })
                  )}
                </>
              )}
            </HandleQuery>
          </div>
        </>
      )}
    </div>
  );
};

export default AccountPackage;
